import { useState, useEffect } from 'react';
import { cn } from '~/lib/utils';
import { BackgroundPattern } from './background-pattern';
import { type CombinedTheme } from './theme-config';
import { useTranslation } from 'react-i18next';
// --- Step 6.1: Import types from store ---
// import type { AvailabilityStatus, Variation } from '~/stores/domain-store'; // Incorrect import
import type { DomainVariation } from '~/types/domain'; // Correct import for Variation type

// Define AvailabilityStatus locally as it's used here and exported via index.tsx
export type AvailabilityStatus =
  | 'available'
  | 'unavailable'
  | 'checking'
  | 'error'
  | 'unchecked' // Added unchecked based on store logic
  | undefined;

interface DomainCardContentProps {
  tld: string; // Keep original TLD for fallback
  theme: CombinedTheme;
  domainBaseName: string;
  // --- Step 6.1: Add/adjust props ---
  availability?: AvailabilityStatus; // Use the prop directly
  domainVariations?: DomainVariation[]; // Use DomainVariation type
}

export const STANDARD_CARD_SIZE = 'text-base sm:text-2xl md:text-3xl';

export function DomainCardContent({
  tld, // Keep original tld prop
  theme,
  domainBaseName,
  availability, // Use the new prop name
  domainVariations = [], // Use the new prop name and type
}: DomainCardContentProps) {
  const { t } = useTranslation();
  const [displayTld, setDisplayTld] = useState(tld || '.com');

  // --- Step 6.2: Remove derived state ---
  // const isAnyVariationAvailable = ...
  // const effectiveAvailabilityStatus = ...
  // const hasErrorState = ...
  // const isCheckingStatus = ...

  // --- Step 6.2: Simplify useEffect based on direct availability prop ---
  useEffect(() => {
    // If available, prioritize the TLD matching the 'tld' prop if that variation is available
    if (availability === 'available') {
      // Find the variation matching the original tld prop (e.g., '.org')
      const matchingVariation = domainVariations?.find(
        (v) => v.name.endsWith(tld) // Check if variation name ends with the tld prop
      );

      // If the matching variation exists and is available, use its TLD
      if (matchingVariation?.available) {
        setDisplayTld(tld); // Use the original tld prop (which includes the dot)
        return; // Stop effect
      }

      // Fallback: If matching variation isn't available, find the *first* available variation
      const firstAvailableVariation = domainVariations?.find(
        (v) => v.available === true
      );
      if (firstAvailableVariation) {
        const parts = firstAvailableVariation.name.split('.');
        const tldPart = parts[parts.length - 1];
        setDisplayTld(`.${tldPart}`); // Reconstruct with a single dot
        return; // Stop effect
      }

      // Final fallback: If no variations are available (shouldn't happen if status is 'available'), use original tld
      setDisplayTld(tld);
      return; // Stop effect
    }

    // If checking, cycle through TLDs from variations
    if (availability === 'checking') {
      const tldList =
        domainVariations && domainVariations.length > 0
          ? domainVariations.map((v) => {
              const parts = v.name.split('.');
              // Correction: Assume parent `tld` prop format is correct. Extract only the TLD part.
              const tldPart = parts[parts.length - 1];
              return `.${tldPart}`; // Reconstruct with a single dot
            })
          : [tld]; // Default to original tld (which should already have a dot)

      if (tldList.length > 1) {
        let currentIndex = 0;
        const intervalId = setInterval(() => {
          currentIndex = (currentIndex + 1) % tldList.length;
          setDisplayTld(tldList[currentIndex]);
        }, 800);
        return () => clearInterval(intervalId);
      } else {
        // If only one TLD, just display it
        setDisplayTld(tldList[0] || tld); // Fallback to original tld
      }
    } else {
      // If unavailable or error, show the original TLD
      setDisplayTld(tld);
    }
  }, [availability, tld, domainVariations]); // Depend on direct props

  // --- Step 6.2: Render based on direct availability prop ---

  if (availability === 'checking') {
    return (
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'flex items-center',
            // Don't use theme font during search
            STANDARD_CARD_SIZE,
            theme.text
          )}
        >
          <span className="font-medium">{domainBaseName}</span>
          <span
            className={cn(
              'tld-animation opacity-70 transition-all',
              'inline-block min-w-[50px] text-left', // Left aligned TLD with min width
              theme.accent.includes('bg-')
                ? theme.accent.replace('bg-', 'text-').split(' ')[0]
                : ''
            )}
          >
            {displayTld}
          </span>
        </div>
        <div className={cn('mt-2 text-[8px] sm:text-xs', theme.text)}>
          {t('domainCard.findingAvailableDomains')}
        </div>
      </div>
    );
  }

  // Determine the final TLD to display when not checking
  // If available, displayTld was set in useEffect. Otherwise, use original tld.
  const finalTld = availability === 'available' ? displayTld : tld;

  return (
    <div
      className={cn(
        'relative z-10 flex flex-col items-center',
        // Apply opacity only when unavailable
        availability === 'unavailable' && 'opacity-75'
      )}
    >
      {/* Background pattern */}
      <BackgroundPattern theme={theme} />

      {/* Background ornament */}
      <div
        className={cn(
          'absolute top-1/2 left-1/2 -z-10 -translate-x-1/2 -translate-y-1/2 rotate-12 transform text-4xl opacity-10 sm:text-7xl',
          theme.font
        )}
      >
        {domainBaseName.charAt(0)}
      </div>

      {/* Main domain display - adjust based on pattern */}
      <div
        className={cn(
          theme.font,
          STANDARD_CARD_SIZE,
          theme.style,
          // --- Step 6.2: Simplify text color logic ---
          availability === 'error'
            ? 'text-red-300' // Error color (use a lighter red on dark background)
            : availability === 'available'
              ? theme.text // Available color (themed)
              : 'text-gray-400', // Unavailable color
          'flex items-center',
          theme.pattern === 'diagonal' ? 'rotate-[-6deg]' : '',
          theme.pattern === 'stacked' ? 'mb-0.5 sm:mb-2' : ''
        )}
      >
        <span className="font-medium">{domainBaseName}</span>
        {/* Display the determined final TLD only if not unavailable */}
        {availability !== 'unavailable' && (
          <span className="lowercase opacity-70">{finalTld}</span>
        )}
      </div>
    </div>
  );
}
